<script setup lang="ts">
import type { DataTableHeaders } from '@/plugins/vuetify'
import type MDBUnité from '@/types/vegco/unite'
import { notifyError, emptyMongoId } from '@/utils/helpers'

const display = useDisplay()
const storeU = useUtilisateurStore()

const siteCS = 'VGCO'

const isLoading = ref(false)
const filter = ref('')
const unités = ref<MDBUnité[]>([])

const filteredUnités = computed(() => {
  const q = filter.value.trim().toLowerCase()
  if (!q) return unités.value
  return unités.value.filter(u =>
    u.symbole.toLowerCase().includes(q) ||
    u.nomCourt.toLowerCase().includes(q) ||
    u.description.toLowerCase().includes(q))
})

const symbolesDisponibles = computed(() => unités.value.map(u => u.symbole).filter(s => s !== ''))
const balisesDisponibles = computed(() => [...new Set(storeU.balises.map(b => b.nom))])

const headers: DataTableHeaders = [
  { title: 'Symbole', key: 'symbole', width: '120px' },
  { title: 'Nom court', key: 'nomCourt', width: '160px' },
  { title: 'Description', key: 'description' },
  { title: 'Unité base', key: 'unitéBase', width: '120px' },
  { title: 'Balise', key: 'balise', width: '120px' },
  { title: 'Qtée par boîte', key: 'quantité', align: 'end', width: '120px' },
  { title: 'Actions', key: 'actions', sortable: false, align: 'end', width: '80px' },
]

async function getData() {
  isLoading.value = true
  try {
    const res = await apiProphet.get(`sites/${siteCS}/unites`)
    unités.value = res.data as MDBUnité[]
  } catch (e) {
    notifyError(e)
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  getData()
  storeU.fetchBalisesIfNeeded()
})

// ── Dialog ────────────────────────────────────────────────────────────────
declare interface UnitéForm {
  id: string
  symbole: string
  nomCourt: string
  description: string
  unitéBase: string
  balise: string
  quantité: number
}

const emptyForm = (): UnitéForm => ({
  id: '',
  symbole: '',
  nomCourt: '',
  description: '',
  unitéBase: '',
  balise: '',
  quantité: 1,
})

const showDialog = ref(false)
const isEditMode = ref(false)
const isSaving = ref(false)
const form = ref<UnitéForm>(emptyForm())

function openCreate() {
  form.value = emptyForm()
  isEditMode.value = false
  showDialog.value = true
}

function openEdit(unité: MDBUnité) {
  form.value = { ...unité }
  isEditMode.value = true
  showDialog.value = true
}

async function saveDialog() {
  if (!form.value.symbole.trim()) {
    notifyError('Le symbole est requis.')
    return
  }

  isSaving.value = true
  try {
    const siteId = await storeU.getOrFetchSiteId(siteCS)
    const payload: MDBUnité = {
      id: form.value.id || emptyMongoId(),
      siteId,
      symbole: form.value.symbole.trim(),
      nomCourt: form.value.nomCourt.trim(),
      description: form.value.description.trim(),
      unitéBase: form.value.unitéBase.trim(),
      balise: form.value.balise.trim(),
      quantité: form.value.quantité,
    }

    if (isEditMode.value) {
      await apiProphet.put(`sites/${siteCS}/unites/${payload.id}`, payload)
      Notify.success('Unité modifiée avec succès.')
    } else {
      await apiProphet.post(`sites/${siteCS}/unites`, { ...payload, id: '' })
      Notify.success('Unité créée avec succès.')
    }

    showDialog.value = false
    await getData()
  } catch (e) {
    notifyError(e)
  } finally {
    isSaving.value = false
  }
}
</script>

<template>
  <div class="pa-4 pa-sm-6">
    <!-- Toolbar -->
    <div class="d-flex align-center flex-wrap mb-4" style="gap: 12px;">
      <v-text-field
        v-model="filter"
        label="Rechercher"
        density="compact"
        variant="outlined"
        rounded="lg"
        hide-details
        prepend-inner-icon="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"
        clearable
        style="max-width: 260px;"/>

      <v-spacer/>

      <v-btn
        color="primary"
        variant="tonal"
        rounded="pill"
        prepend-icon="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z"
        :disabled="isLoading"
        @click="openCreate">
        Nouvelle unité
      </v-btn>
    </div>

    <v-data-table
      :headers="headers"
      :items="filteredUnités"
      :loading="isLoading"
      :hide-default-header="display.smAndDown.value"
      item-value="id"
      density="comfortable"
      hover>

      <!-- Mobile card rows -->
      <template v-if="display.smAndDown.value" #item="{ item }">
        <div class="unite-mobile-card ma-2 pa-3">
          <div class="d-flex align-center justify-space-between mb-2">
            <div style="min-width: 0;">
              <span class="text-subtitle-2 font-weight-bold text-truncate">{{ item.symbole }}</span>
              <div class="text-caption text-medium-emphasis text-truncate">{{ item.nomCourt || '—' }}</div>
            </div>
            <v-btn icon="M14.06,9L15,9.94L5.92,19H5V18.08L14.06,9M17.66,3C17.41,3 17.15,3.1 16.96,3.29L15.13,5.12L18.88,8.87L20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18.17,3.09 17.92,3 17.66,3M14.06,6.19L3,17.25V21H6.75L17.81,9.94L14.06,6.19Z" variant="tonal" color="primary" size="small" class="flex-shrink-0 ms-2" @click="openEdit(item)"/>
          </div>
          <v-divider class="mb-2"/>
          <div class="text-caption text-medium-emphasis">{{ item.description || '—' }}</div>
          <div class="d-flex flex-wrap mt-1" style="gap: 8px;">
            <span class="text-caption">Base: {{ item.unitéBase || '—' }}</span>
            <span class="text-caption">Balise: {{ item.balise || '—' }}</span>
            <span class="text-caption">Qtée par boîte: {{ item.quantité }}</span>
          </div>
        </div>
      </template>

      <template #item.actions="{ item }">
        <v-btn icon="M14.06,9L15,9.94L5.92,19H5V18.08L14.06,9M17.66,3C17.41,3 17.15,3.1 16.96,3.29L15.13,5.12L18.88,8.87L20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18.17,3.09 17.92,3 17.66,3M14.06,6.19L3,17.25V21H6.75L17.81,9.94L14.06,6.19Z" variant="text" size="small" color="primary" @click="openEdit(item)"/>
      </template>

      <template #no-data>
        <div class="text-center py-8 text-medium-emphasis">
          <v-icon icon="M20 19.88V22L18.2 20.83L13.41 11.83A4.94 4.94 0 0 0 15.19 10.83M15 7A3 3 0 0 1 12 10A3.27 3.27 0 0 1 11.56 10L5.8 20.83L4 22V19.88L9.79 9A3 3 0 0 1 12 4V2A1 1 0 0 1 13 3V4.18A3 3 0 0 1 15 7M13 7A1 1 0 1 0 12 8A1 1 0 0 0 13 7M4.22 10L6 11.8L4.56 14.56L2.1 12.1M12 17.76L10.5 16.25L9 19L12 22L15 19L13.53 16.23M19.78 10L18 11.8L19.5 14.56L21.9 12.1Z" size="40" class="mb-2"/>
          <div>Aucune unité trouvée</div>
        </div>
      </template>
    </v-data-table>

    <!-- ── Create / Edit dialog ──────────────────────────────────────────── -->
    <v-dialog v-model="showDialog" :fullscreen="display.smAndDown.value" :max-width="display.smAndDown.value ? undefined : 560" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center pa-4" style="gap: 10px;">
          <v-avatar color="primary" variant="tonal" size="36" rounded="lg">
            <v-icon :icon="isEditMode ? 'M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z' : 'M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z'" size="20"/>
          </v-avatar>
          <span class="text-h6">{{ isEditMode ? 'Modifier l\'unité' : 'Ajouter une unité' }}</span>
          <v-spacer/>
          <v-btn icon="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" variant="text" @click="showDialog = false"/>
        </v-card-title>

        <v-divider/>

        <v-card-text class="pa-4">
          <v-container class="pa-0">
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.symbole" label="Symbole *" variant="outlined" density="compact" rounded="lg"
                  prepend-inner-icon="M21.41 11.58L12.41 2.58A2 2 0 0 0 11 2H4A2 2 0 0 0 2 4V11A2 2 0 0 0 2.59 12.42L11.59 21.42A2 2 0 0 0 13 22A2 2 0 0 0 14.41 21.41L21.41 14.41A2 2 0 0 0 22 13A2 2 0 0 0 21.41 11.58M13 20L4 11V4H11L20 13M6.5 5A1.5 1.5 0 1 1 5 6.5A1.5 1.5 0 0 1 6.5 5Z" hide-details="auto"/>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.nomCourt" label="Nom court" variant="outlined" density="compact" rounded="lg"
                  prepend-inner-icon="M16,17H5V7H16L19.55,12M17.63,5.84C17.27,5.33 16.67,5 16,5H5A2,2 0 0,0 3,7V17A2,2 0 0,0 5,19H16C16.67,19 17.27,18.66 17.63,18.15L22,12L17.63,5.84Z" hide-details="auto"/>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="form.description" label="Description" variant="outlined" density="compact" rounded="lg"
                  prepend-inner-icon="M4,5H20V7H4V5M4,9H20V11H4V9M4,13H20V15H4V13M4,17H14V19H4V17Z" hide-details="auto"/>
              </v-col>
              <v-col cols="12" sm="6">
                <v-combobox
                  v-model="form.unitéBase" :items="symbolesDisponibles" label="Unité base" variant="outlined"
                  density="compact" rounded="lg" prepend-inner-icon="M1.39,18.36L3.16,16.6L4.58,18L5.64,16.95L4.22,15.54L5.64,14.12L8.11,16.6L9.17,15.54L6.7,13.06L8.11,11.65L9.53,13.06L10.59,12L9.17,10.59L10.59,9.17L13.06,11.65L14.12,10.59L11.65,8.11L13.06,6.7L14.47,8.11L15.54,7.05L14.12,5.64L15.54,4.22L18,6.7L19.07,5.64L16.6,3.16L18.36,1.39L22.61,5.64L5.64,22.61L1.39,18.36Z" hide-details="auto" clearable/>
              </v-col>
              <v-col cols="12" sm="6">
                <v-combobox
                  v-model="form.balise" :items="balisesDisponibles" label="Balise" variant="outlined"
                  density="compact" rounded="lg" prepend-inner-icon="M6.5 10C7.3 10 8 9.3 8 8.5S7.3 7 6.5 7 5 7.7 5 8.5 5.7 10 6.5 10M9 6L16 13L11 18L4 11V6H9M9 4H4C2.9 4 2 4.9 2 6V11C2 11.6 2.2 12.1 2.6 12.4L9.6 19.4C9.9 19.8 10.4 20 11 20S12.1 19.8 12.4 19.4L17.4 14.4C17.8 14 18 13.5 18 13C18 12.4 17.8 11.9 17.4 11.6L10.4 4.6C10.1 4.2 9.6 4 9 4M13.5 5.7L14.5 4.7L21.4 11.6C21.8 12 22 12.5 22 13S21.8 14.1 21.4 14.4L16 19.8L15 18.8L20.7 13L13.5 5.7Z" hide-details="auto" clearable/>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model.number="form.quantité" label="Qtée par boîte" type="number" variant="outlined" density="compact"
                  rounded="lg" prepend-inner-icon="M4,17V9H2V7H6V17H4M22,15C22,16.11 21.1,17 20,17H16V15H20V13H18V11H20V9H16V7H20A2,2 0 0,1 22,9V10.5A1.5,1.5 0 0,1 20.5,12A1.5,1.5 0 0,1 22,13.5V15M14,15V17H8V13C8,11.89 8.9,11 10,11H12V9H8V7H12A2,2 0 0,1 14,9V11C14,12.11 13.1,13 12,13H10V15H14Z" hide-details="auto"/>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-divider/>

        <v-card-actions class="pa-4">
          <v-spacer/>
          <v-btn variant="text" @click="showDialog = false">Annuler</v-btn>
          <v-btn color="primary" variant="flat" rounded="pill" prepend-icon="M17 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V7L17 3M19 19H5V5H16.17L19 7.83V19M12 12C10.34 12 9 13.34 9 15S10.34 18 12 18 15 16.66 15 15 13.66 12 12 12M6 6H15V10H6V6Z" :loading="isSaving" @click="saveDialog">
            Enregistrer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.unite-mobile-card {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
}
</style>
